/**
 * The destinations as a pager: the next one is really there, under your
 * thumb, and the drag carries it in.
 *
 * The page has no horizontal scroll of its own — `app.css` clips it, and the
 * one thing that genuinely needs more width than a phone has (the week grid on
 * Plan) scrolls inside its own box. That left a sideways gesture meaning
 * nothing, so it means the obvious thing instead: left goes to the next of
 * Today · Foods · Prepare · Plan · Review · Weight · You, right to the
 * previous, in the order the tab bar already shows. The gesture and the bar agree, which is the only
 * reason a swipe is learnable without being taught.
 *
 * ## Why the neighbour is mounted
 *
 * The first version of this animated *afterwards* — the screen you had already
 * arrived at slid in from the side. It told you which way you had gone, but not
 * where you were going, and a gesture you cannot preview is a gesture you
 * cannot abort. You had to commit to find out.
 *
 * So the neighbour is rendered for real, one pane over, and the track follows
 * your thumb one-to-one. You see Plan's week before you have decided to go to
 * Plan, and you can put it back. The commit is then not a jump but the rest of
 * a movement already half-made: the track finishes its travel, and the pane you
 * were looking at is simply the pane you are on.
 *
 * It is mounted lazily — only once a drag has declared itself horizontal — so
 * the neighbour's queries do not fire on every tap and scroll. They are cached
 * by the time you arrive, which is a second reason to do it this way.
 *
 * At the two ends there is no neighbour to mount. The track resists instead,
 * moving a damped fraction of the drag: the stiffness is the message, and it
 * replaces the browser's old meaningless rubber-band with one that means "this
 * way is the end".
 *
 * ## What it deliberately does not do
 *
 * - **Wrap.** The ends are ends. Sliding past "You" onto "Today" makes the row
 *   of tabs a lie about where you are in it.
 * - **Page the pushed screens.** The food editor, the recipe editor — those are
 *   not sections, they are things you are in the middle of, and throwing away a
 *   half-filled form on a stray thumb is unforgivable. The add sheet is not a
 *   route at all any more, so it cannot be paged off: it sits *over* Today, and
 *   a drag under it is a drag on a layer that is not there.
 * - **Fight a scroller.** A gesture that starts inside something that can
 *   itself scroll sideways belongs to that thing, and one starting in a text
 *   field belongs to the caret.
 * - **Animate at all under `prefers-reduced-motion`.** No peek, no travel: the
 *   commit is a plain navigation.
 */

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { NAV } from "./routes";

/** Before this, a drag is a tap that slid and nothing should move. */
const WAKE_PX = 10;
/** How much more horizontal than vertical, so a diagonal scroll scrolls. */
const SLOPE = 1.4;
/** Past this much of the pane, letting go completes rather than returns. */
const COMMIT_FRACTION = 0.28;
/** ...but never less than this, so a short pane is not committed by a twitch. */
const COMMIT_MIN_PX = 64;

/** The gutter between panes. Must match `--sp-5`, which `.pager__peek` uses. */
const GUTTER_PX = 24;

/** Finishing the travel after you let go, and giving up and putting it back. */
const COMMIT_MS = 220;
const SETTLE_MS = 190;
const EASE = "cubic-bezier(0.2, 0.7, 0.3, 1)";

/** At an end there is nothing to reveal, so the track barely gives. */
const RESIST = 0.16;
const RESIST_MAX_PX = 14;

/**
 * Trackpad equivalents. Deliberately further than a touch swipe: a two-finger
 * scroll drifts sideways constantly, and navigating on drift would be worse
 * than not navigating at all. A wheel has no "in progress" to preview, so it
 * navigates outright and the arrival animation carries the direction.
 */
const WHEEL_PX = 140;
const WHEEL_GAP_MS = 220;
const WHEEL_COOLDOWN_MS = 700;

/** The arrival's length. Must match `section-enter-*` in `app.css`. */
const ENTER_MS = 240;

/**
 * Which of the seven destinations a path belongs to, or −1 for none.
 *
 * Exact, with one exception: `/day/2026-08-20` is Today, because it is the same
 * screen showing a different day and paging off it should behave the way paging
 * off today does.
 *
 * Nothing else counts — not `/plan/shopping`, not `/recipes/new`, not
 * `/foods/new`, not `/prepare/new`. Being one URL segment deeper is not what
 * makes something a sub-screen; being something you are partway through is.
 * That last one is load-bearing: a thumb dragging across the pot builder must
 * not page the pot away.
 *
 * `/foods` is a section and `/foods/:id` is not, which is the rule stated
 * twice: the library is a list you read, and the editor is a form you are
 * halfway down.
 */
export function sectionIndex(pathname: string): number {
  if (pathname.startsWith("/day/")) return 0;
  return NAV.findIndex(({ to }) => pathname === to);
}

/** The neighbour currently rendered beside the screen you are on. */
export interface Peek {
  /** Which way it lies: +1 the next destination, −1 the previous. */
  direction: 1 | -1;
  /** Its path, fixed when the drag opened it so a re-render cannot move it. */
  to: string;
  /**
   * Where the viewport was when the drag began.
   *
   * The peek is absolutely positioned inside a container whose top is the top
   * of the *document*, so on a page scrolled halfway down it would otherwise be
   * mounted above the screen and slide in showing nothing. Offsetting it by the
   * scroll position lines its first line up with what you can actually see —
   * and arriving at the top of a section is right anyway, which is why the
   * commit scrolls there.
   */
  top: number;
}

/** Whether a gesture starting here belongs to something other than the page. */
function claimedByContent(target: EventTarget | null): boolean {
  let node = target instanceof Element ? target : null;
  while (node) {
    const tag = node.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if (node.scrollWidth > node.clientWidth + 1) {
      const overflowX = getComputedStyle(node).overflowX;
      if (overflowX === "auto" || overflowX === "scroll") return true;
    }
    node = node.parentElement;
  }
  return false;
}

function stillMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

export interface SectionPager {
  /** Attach to the element holding both panes. Its transform is the travel. */
  track: RefObject<HTMLDivElement | null>;
  /** The neighbour to render, or null when no drag is open. */
  peek: Peek | null;
}

export function useSectionPager(): SectionPager {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const track = useRef<HTMLDivElement | null>(null);

  const [peek, setPeek] = useState<Peek | null>(null);
  /** The same value, for handlers that must see it without a re-render. */
  const open = useRef<Peek | null>(null);
  /** Set when the navigation about to happen is the end of a drag. */
  const completing = useRef(false);
  /** The section we were on, to know which way the next one arrived from. */
  const previous = useRef<number>(sectionIndex(pathname));

  const settle = (element: HTMLDivElement, ms: number, to: string) => {
    element.style.transition = `transform ${ms}ms ${EASE}`;
    element.style.transform = to;
  };

  const clear = (element: HTMLDivElement) => {
    element.style.transition = "";
    element.style.transform = "";
  };

  /**
   * Land the new location, before the browser paints it.
   *
   * A layout effect rather than an effect: between `navigate` and this, React
   * has already rendered the arriving screen into a track still translated a
   * whole pane sideways. Resetting after paint would show that frame.
   */
  useLayoutEffect(() => {
    const index = sectionIndex(pathname);
    const from = previous.current;
    previous.current = index;

    const element = track.current;
    if (!element) return;

    if (completing.current) {
      // The drag already performed the motion; the pane under your thumb is
      // now the pane you are on, so there is nothing to animate and everything
      // to put back where it started.
      completing.current = false;
      clear(element);
      element.removeAttribute("data-enter");
      setPeek(null);
      open.current = null;
      window.scrollTo(0, 0);
      return;
    }

    // Any other navigation supersedes a drag that never finished — a touch the
    // OS swallowed, a link followed from inside a half-open peek. Without this
    // the neighbour stays mounted showing the *old* screen's neighbour, which
    // is not merely stale but wrong: a peek captioned Review, one pane over
    // from a Today you have already left.
    if (open.current) {
      open.current = null;
      setPeek(null);
    }

    // Arrived some other way — a tap on the tab bar, the back button, a
    // trackpad. There was no gesture to preview it, so the screen slides in
    // from the side it would have come from. The direction describes the move,
    // not the input that caused it: animating only the swipe would teach that
    // the bar and the gesture are different mechanisms.
    if (index < 0 || from < 0 || index === from) return;

    clear(element);
    element.removeAttribute("data-enter");
    // Reading a layout property between the removal and the re-add is what
    // makes those two separate changes rather than one no-op, which is the
    // difference between the animation restarting and not playing at all.
    void element.offsetWidth;
    element.dataset.enter = index > from ? "next" : "prev";

    const done = () => element.removeAttribute("data-enter");
    element.addEventListener("animationend", done, { once: true });
    element.addEventListener("animationcancel", done, { once: true });
    // A backstop, because the failure is asymmetric: the keyframes hold the
    // screen aside and dimmed, so an end event that never arrives would leave
    // the app looking broken with no way back.
    const failsafe = window.setTimeout(done, ENTER_MS * 4);
    return () => {
      window.clearTimeout(failsafe);
      element.removeEventListener("animationend", done);
      element.removeEventListener("animationcancel", done);
    };
  }, [pathname]);

  useEffect(() => {
    const index = sectionIndex(pathname);
    if (index < 0) return;

    const neighbour = (direction: 1 | -1) => NAV[index + direction]?.to ?? null;

    /** How far a pane has to travel to be gone: its width plus the gutter. */
    const span = () => (track.current?.clientWidth ?? 0) + GUTTER_PX;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let awake = false;

    const reset = () => {
      tracking = false;
      awake = false;
    };

    const onTouchStart = (event: TouchEvent) => {
      reset();
      // A previous gesture that was never finished — the OS took the touch, the
      // browser went to the app switcher — has left a pane hanging. A new touch
      // supersedes it rather than resuming it.
      const stranded = track.current;
      if (stranded && open.current) {
        clear(stranded);
        open.current = null;
        setPeek(null);
      }
      // A second finger means a pinch, which is not this.
      if (event.touches.length !== 1) return;
      if (claimedByContent(event.target)) return;
      const touch = event.touches[0]!;
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!tracking) return;
      const element = track.current;
      const touch = event.touches[0];
      if (!element || !touch) return;

      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      // Only once the gesture has declared itself horizontal. Reacting to the
      // first pixel would mount a whole screen every time somebody read down
      // the page.
      if (Math.abs(dx) < WAKE_PX || Math.abs(dx) < Math.abs(dy) * SLOPE) return;
      if (stillMotion()) return;
      awake = true;

      const direction: 1 | -1 = dx < 0 ? 1 : -1;
      const to = neighbour(direction);

      // Dragged back through zero and out the other side: the neighbour on
      // this side is a different screen, so swap it.
      if (to && open.current?.direction !== direction) {
        const next: Peek = { direction, to, top: window.scrollY };
        open.current = next;
        setPeek(next);
      }

      element.style.transition = "";
      if (to) {
        // One to one. The pane is really there; anything less would be the
        // screen lagging the thumb for no reason.
        const limit = span();
        element.style.transform = `translate3d(${Math.max(-limit, Math.min(limit, dx)).toFixed(1)}px, 0, 0)`;
      } else {
        // Nothing this way. It gives a little and no more.
        const give = Math.sign(dx) * Math.min(Math.abs(dx) * RESIST, RESIST_MAX_PX);
        element.style.transform = `translate3d(${give.toFixed(1)}px, 0, 0)`;
      }
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (!tracking) return;
      const element = track.current;
      const touch = event.changedTouches[0];
      const wasAwake = awake;
      const showing = open.current;
      reset();
      if (!element || !touch) return;

      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const horizontal = Math.abs(dx) >= Math.abs(dy) * SLOPE;
      const far = Math.abs(dx) >= Math.max(COMMIT_MIN_PX, span() * COMMIT_FRACTION);
      const forwards = showing !== null && showing.direction === (dx < 0 ? 1 : -1);

      if (!wasAwake && !element.style.transform) {
        // Never woke: an ordinary tap or a vertical scroll. Nothing was moved
        // and nothing needs putting back.
        if (horizontal && far) navigateTo(dx < 0 ? 1 : -1);
        return;
      }

      if (horizontal && far && forwards && showing) {
        // Finish the travel, then swap. The pane you are looking at when the
        // transition ends is the pane you land on, so there is no moment where
        // the screen changes under you.
        completing.current = true;
        const to = showing.to;
        const distance = showing.direction > 0 ? -span() : span();
        settle(element, COMMIT_MS, `translate3d(${distance}px, 0, 0)`);

        let landed = false;
        const land = () => {
          if (landed) return;
          landed = true;
          element.removeEventListener("transitionend", land);
          navigate(to);
        };
        element.addEventListener("transitionend", land);
        // `transitionend` does not fire if the property never changes — a track
        // already at the far edge, a browser that skipped the transition. The
        // timer is what stops that from stranding the app one pane over.
        window.setTimeout(land, COMMIT_MS + 80);
        return;
      }

      // Put it back, and only then drop the neighbour: unmounting it first
      // would leave a blank pane sliding back into place.
      settle(element, SETTLE_MS, "");
      window.setTimeout(() => {
        const current = track.current;
        if (current === element) current.style.transition = "";
        if (!open.current) return;
        open.current = null;
        setPeek(null);
      }, SETTLE_MS);
    };

    const onTouchCancel = () => {
      const element = track.current;
      reset();
      if (!element) return;
      settle(element, SETTLE_MS, "");
      window.setTimeout(() => {
        open.current = null;
        setPeek(null);
      }, SETTLE_MS);
    };

    /** +1 is the next destination in the bar; −1 the previous. Ends stop. */
    const navigateTo = (direction: 1 | -1) => {
      const to = neighbour(direction);
      if (to) navigate(to);
    };

    let travel = 0;
    let lastWheel = 0;
    let quietUntil = 0;

    const onWheel = (event: WheelEvent) => {
      const now = event.timeStamp;
      if (now - lastWheel > WHEEL_GAP_MS) travel = 0;
      lastWheel = now;

      // A vertical-dominant event is a scroll, and it also ends whatever
      // sideways drift had accumulated before it.
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
        travel = 0;
        return;
      }
      if (now < quietUntil || claimedByContent(event.target)) return;

      travel += event.deltaX;
      if (Math.abs(travel) < WHEEL_PX) return;
      const direction = travel > 0 ? 1 : -1;
      travel = 0;
      quietUntil = now + WHEEL_COOLDOWN_MS;
      navigateTo(direction);
    };

    // Passive: none of these ever call `preventDefault`, and saying so keeps
    // the browser's scrolling off the main thread. Nothing here needs to cancel
    // a scroll, because with the page clipped there is no sideways scroll for
    // the browser to start.
    const options = { passive: true } as const;
    window.addEventListener("touchstart", onTouchStart, options);
    window.addEventListener("touchmove", onTouchMove, options);
    window.addEventListener("touchend", onTouchEnd, options);
    window.addEventListener("touchcancel", onTouchCancel, options);
    window.addEventListener("wheel", onWheel, options);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchCancel);
      window.removeEventListener("wheel", onWheel);
    };
  }, [pathname, navigate]);

  return { track, peek };
}
